// src/components/RadialStatusMenu.js
import React, { useState, useEffect, useMemo, useRef } from 'react';
import { 
    FiInbox, FiPlayCircle, FiPauseCircle, FiUser, FiPackage, FiCheckCircle, FiX 
} from 'react-icons/fi';

// --- GEOMETRY CONSTANTS ---
const MENU_SIZE = 350; // Scaled down for mobile fit
const CENTER = MENU_SIZE / 2;
const OUTER_RADIUS = 130;
const INNER_RADIUS = 50;
const ICON_RADIUS = (OUTER_RADIUS + INNER_RADIUS) / 2;

// --- HELPER: POLAR TO CARTESIAN ---
const polarToCartesian = (centerX, centerY, radius, angleInDegrees) => {
    const angleInRadians = (angleInDegrees - 90) * Math.PI / 180.0;
    return {
        x: centerX + (radius * Math.cos(angleInRadians)),
        y: centerY + (radius * Math.sin(angleInRadians))
    };
};

// --- HELPER: SVG ARC GENERATOR ---
const createDonutSlice = (x, y, startAngle, endAngle, innerR, outerR) => {
    const start = polarToCartesian(x, y, outerR, endAngle);
    const end = polarToCartesian(x, y, outerR, startAngle);
    const startInner = polarToCartesian(x, y, innerR, endAngle);
    const endInner = polarToCartesian(x, y, innerR, startAngle);

    const largeArcFlag = endAngle - startAngle <= 180 ? "0" : "1";

    return [
        "M", start.x, start.y,
        "A", outerR, outerR, 0, largeArcFlag, 0, end.x, end.y,
        "L", endInner.x, endInner.y,
        "A", innerR, innerR, 0, largeArcFlag, 1, startInner.x, startInner.y,
        "Z"
    ].join(" ");
};

// --- CSS ---
const radialStyles = `
  .radial-overlay {
    position: fixed; top: 0; left: 0; width: 100vw; height: 100vh;
    z-index: 9999;
    background-color: rgba(20, 20, 20, 0.2);
    cursor: crosshair;
    user-select: none;
    touch-action: none; /* Prevents mobile scrolling while interacting */
    -webkit-tap-highlight-color: transparent; /* Removes mobile tap highlight */
    display: block;
    opacity: 0; animation: fadeIn 0.1s ease-out forwards;
  }

  .radial-menu-wrapper {
    position: absolute;
    width: ${MENU_SIZE}px; 
    height: ${MENU_SIZE}px;
    pointer-events: none; 
    filter: drop-shadow(0 15px 35px rgba(0,0,0,0.3));
    transform-origin: center;
    animation: menuPop 0.15s cubic-bezier(0.175, 0.885, 0.32, 1.275) forwards;
    overflow: visible;
  }

  @keyframes fadeIn { to { opacity: 1; } }
  @keyframes menuPop {
    0% { transform: scale(0.5); opacity: 0; }
    100% { transform: scale(1); opacity: 1; }
  }

  .sector-group {
    cursor: pointer;
    pointer-events: auto;
    transition: transform 0.15s cubic-bezier(0.175, 0.885, 0.32, 1.275);
    transform-origin: ${CENTER}px ${CENTER}px;
  }

  .sector-group.active {
    transform: scale(1.1);
    z-index: 10;
    filter: drop-shadow(0 0 8px rgba(0,0,0,0.2));
  }

  .sector-path {
    stroke-width: 2px;
    stroke-linejoin: round;
    transition: fill 0.15s, stroke 0.15s;
  }

  .sector-text {
    font-family: -apple-system, BlinkMacSystemFont, "Segoe UI", Roboto, sans-serif;
    font-size: 11px;
    font-weight: 700;
    text-anchor: middle;
    text-transform: uppercase;
    letter-spacing: 0.5px;
    fill: #555;
    pointer-events: none;
    transition: fill 0.15s;
  }

  .sector-icon-container {
    color: #555;
    transition: color 0.15s;
    pointer-events: none;
    display: flex; justify-content: center; align-items: center;
  }

  /* --- TICKET STATUS STYLES --- */
  .sector-group.Open .sector-path { fill: rgba(241, 245, 249, 0.95); stroke: #94a3b8; }
  .sector-group.Open .sector-text, .sector-group.Open .sector-icon-container { color: #475569; fill: #475569; }
  .sector-group.Open.active .sector-path { fill: #475569; stroke: #475569; }
  .sector-group.Open.active .sector-text, .sector-group.Open.active .sector-icon-container { color: white; fill: white; }

  .sector-group.In-Progress .sector-path { fill: rgba(255, 247, 237, 0.95); stroke: #f97316; }
  .sector-group.In-Progress .sector-text, .sector-group.In-Progress .sector-icon-container { color: #c2410c; fill: #c2410c; }
  .sector-group.In-Progress.active .sector-path { fill: #f97316; stroke: #f97316; }
  .sector-group.In-Progress.active .sector-text, .sector-group.In-Progress.active .sector-icon-container { color: white; fill: white; }

  .sector-group.Pending .sector-path, .sector-group.Waiting-On-Client .sector-path, .sector-group.Waiting-On-Parts .sector-path { fill: rgba(254, 252, 232, 0.95); stroke: #eab308; }
  .sector-group.Pending .sector-text, .sector-group.Waiting-On-Client .sector-text, .sector-group.Waiting-On-Parts .sector-text, .sector-group.Pending .sector-icon-container, .sector-group.Waiting-On-Client .sector-icon-container, .sector-group.Waiting-On-Parts .sector-icon-container { color: #a16207; fill: #a16207; }
  .sector-group.Pending.active .sector-path, .sector-group.Waiting-On-Client.active .sector-path, .sector-group.Waiting-On-Parts.active .sector-path { fill: #eab308; stroke: #eab308; }
  .sector-group.Pending.active .sector-text, .sector-group.Waiting-On-Client.active .sector-text, .sector-group.Waiting-On-Parts.active .sector-text, .sector-group.Pending.active .sector-icon-container, .sector-group.Waiting-On-Client.active .sector-icon-container, .sector-group.Waiting-On-Parts.active .sector-icon-container { color: white; fill: white; }

  .sector-group.Closed .sector-path { fill: rgba(240, 253, 244, 0.95); stroke: #22c55e; }
  .sector-group.Closed .sector-text, .sector-group.Closed .sector-icon-container { color: #15803d; fill: #15803d; }
  .sector-group.Closed.active .sector-path { fill: #22c55e; stroke: #22c55e; }
  .sector-group.Closed.active .sector-text, .sector-group.Closed.active .sector-icon-container { color: white; fill: white; }

  /* --- PRIORITY STYLES --- */
  .sector-group.Critical .sector-path { fill: rgba(254, 226, 226, 0.95); stroke: #ef4444; }
  .sector-group.Critical .sector-text, .sector-group.Critical .sector-icon-container { color: #b91c1c; fill: #b91c1c; }
  .sector-group.Critical.active .sector-path { fill: #ef4444; stroke: #ef4444; }
  .sector-group.Critical.active .sector-text, .sector-group.Critical.active .sector-icon-container { color: white; fill: white; }

  .sector-group.High .sector-path { fill: rgba(255, 237, 213, 0.95); stroke: #f97316; }
  .sector-group.High .sector-text, .sector-group.High .sector-icon-container { color: #c2410c; fill: #c2410c; }
  .sector-group.High.active .sector-path { fill: #f97316; stroke: #f97316; }
  .sector-group.High.active .sector-text, .sector-group.High.active .sector-icon-container { color: white; fill: white; }

  .sector-group.Medium .sector-path { fill: rgba(254, 249, 195, 0.95); stroke: #eab308; }
  .sector-group.Medium .sector-text, .sector-group.Medium .sector-icon-container { color: #a16207; fill: #a16207; }
  .sector-group.Medium.active .sector-path { fill: #eab308; stroke: #eab308; }
  .sector-group.Medium.active .sector-text, .sector-group.Medium.active .sector-icon-container { color: white; fill: white; }

  .sector-group.Low .sector-path { fill: rgba(220, 252, 231, 0.95); stroke: #22c55e; }
  .sector-group.Low .sector-text, .sector-group.Low .sector-icon-container { color: #15803d; fill: #15803d; }
  .sector-group.Low.active .sector-path { fill: #22c55e; stroke: #22c55e; }
  .sector-group.Low.active .sector-text, .sector-group.Low.active .sector-icon-container { color: white; fill: white; }

  /* --- TECHNICIAN STATUS STYLES --- */
  .sector-group.Active .sector-path { fill: rgba(220, 252, 231, 0.95); stroke: #22c55e; }
  .sector-group.Active .sector-text, .sector-group.Active .sector-icon-container { color: #15803d; fill: #15803d; }
  .sector-group.Active.active .sector-path { fill: #22c55e; stroke: #22c55e; }
  .sector-group.Active.active .sector-text, .sector-group.Active.active .sector-icon-container { color: white; fill: white; }

  .sector-group.On-Leave .sector-path { fill: rgba(255, 237, 213, 0.95); stroke: #f97316; }
  .sector-group.On-Leave .sector-text, .sector-group.On-Leave .sector-icon-container { color: #c2410c; fill: #c2410c; }
  .sector-group.On-Leave.active .sector-path { fill: #f97316; stroke: #f97316; }
  .sector-group.On-Leave.active .sector-text, .sector-group.On-Leave.active .sector-icon-container { color: white; fill: white; }

  .sector-group.Inactive .sector-path { fill: rgba(241, 245, 249, 0.95); stroke: #94a3b8; }
  .sector-group.Inactive .sector-text, .sector-group.Inactive .sector-icon-container { color: #475569; fill: #475569; }
  .sector-group.Inactive.active .sector-path { fill: #475569; stroke: #475569; }
  .sector-group.Inactive.active .sector-text, .sector-group.Inactive.active .sector-icon-container { color: white; fill: white; }

  /* --- CENTER CIRCLE --- */
  .center-group { cursor: pointer; pointer-events: auto; transition: transform 0.1s; transform-origin: ${CENTER}px ${CENTER}px; }
  .center-circle { fill: white; stroke: #e5e7eb; stroke-width: 2px; transition: fill 0.2s, stroke 0.2s; }
  .center-icon { color: #9ca3af; display: flex; justify-content: center; align-items: center; }
  .center-group.active { transform: scale(0.9); }
  .center-group.active .center-circle { fill: #ef4444; stroke: #ef4444; }
  .center-group.active .center-icon { color: white; }

  /* --- DARK MODE OVERRIDES --- */
  body.dark-mode .center-circle { fill: #1f2937; stroke: #374151; }
  body.dark-mode .sector-text { fill: #cbd5e1; }
  
  body.dark-mode .sector-group.Open .sector-path, 
  body.dark-mode .sector-group.Inactive .sector-path { fill: rgba(30, 41, 59, 0.95); stroke: #475569; }
  
  body.dark-mode .sector-group.In-Progress .sector-path { fill: rgba(67, 20, 7, 0.6); }
  
  body.dark-mode .sector-group.Pending .sector-path, 
  body.dark-mode .sector-group.Waiting-On-Client .sector-path, 
  body.dark-mode .sector-group.Waiting-On-Parts .sector-path { fill: rgba(66, 32, 6, 0.6); }
  
  body.dark-mode .sector-group.Closed .sector-path,
  body.dark-mode .sector-group.Active .sector-path { fill: rgba(6, 78, 59, 0.6); }

  body.dark-mode .sector-group.On-Leave .sector-path { fill: rgba(66, 32, 6, 0.6); stroke: #f97316; }

  body.dark-mode .sector-group.Critical .sector-path { fill: rgba(127, 29, 29, 0.6); stroke: #ef4444; }
  body.dark-mode .sector-group.High .sector-path { fill: rgba(124, 45, 18, 0.6); stroke: #f97316; }
  body.dark-mode .sector-group.Medium .sector-path { fill: rgba(113, 63, 18, 0.6); stroke: #eab308; }
  body.dark-mode .sector-group.Low .sector-path { fill: rgba(20, 83, 45, 0.6); stroke: #22c55e; }
`;

const RadialStatusMenu = ({ options, onSelect, onClose, clickPosition }) => {
    const [activeIndex, setActiveIndex] = useState(null);
    const [isCenterActive, setIsCenterActive] = useState(true);
    const [isSticky, setIsSticky] = useState(false);
    const mountTimeRef = useRef(Date.now());

    // Use passed options or fallback to default Status options
    const menuItems = useMemo(() => {
        if (options && options.length > 0) return options;
        return [
            { label: 'Open', icon: FiInbox },
            { label: 'In Progress', icon: FiPlayCircle },
            { label: 'Pending', icon: FiPauseCircle },
            { label: 'Waiting On Client', icon: FiUser },
            { label: 'Waiting On Parts', icon: FiPackage },
            { label: 'Closed', icon: FiCheckCircle },
        ];
    }, [options]);

    const SECTOR_ANGLE = 360 / menuItems.length;
    const GAP = 2;

    const menuStyle = useMemo(() => {
        const vw = document.documentElement.clientWidth;
        const vh = document.documentElement.clientHeight;
        
        let x = clickPosition?.x || vw / 2;
        let y = clickPosition?.y || vh / 2;

        const radius = MENU_SIZE / 2;
        const padding = 10;
        
        if (x - radius - padding < 0) x = radius + padding;
        if (x + radius + padding > vw) x = vw - radius - padding;
        if (y - radius - padding < 0) y = radius + padding;
        if (y + radius + padding > vh) y = vh - radius - padding;

        return { left: x - radius, top: y - radius };
    }, [clickPosition]);

    useEffect(() => {
        // --- POWERFUL TAP & HOVER CALCULATOR ---
        // This function takes coordinates, figures out if they are over a slice, 
        // and returns the state without depending on dragging
        const calculateHoverState = (clientX, clientY) => {
            const menuCenterX = menuStyle.left + CENTER;
            const menuCenterY = menuStyle.top + CENTER;

            const dx = clientX - menuCenterX;
            const dy = clientY - menuCenterY;
            const dist = Math.sqrt(dx * dx + dy * dy);

            if (dist < INNER_RADIUS) {
                setIsCenterActive(true);
                setActiveIndex(null);
                return { isCenter: true, index: null, isOutside: false };
            }

            // Mobile screens need a generous outer boundary to not accidentally close
            if (dist > OUTER_RADIUS + 40) { 
                setIsCenterActive(false);
                setActiveIndex(null);
                return { isCenter: false, index: null, isOutside: true };
            }

            setIsCenterActive(false);
            let angleDeg = (Math.atan2(dy, dx) * 180 / Math.PI) + 90;
            if (angleDeg < 0) angleDeg += 360;

            const index = Math.floor(angleDeg / SECTOR_ANGLE) % menuItems.length;
            setActiveIndex(index);
            return { isCenter: false, index, isOutside: false };
        };

        const onMouseMove = (e) => calculateHoverState(e.clientX, e.clientY);
        const onTouchMove = (e) => {
            if (e.touches.length > 0) {
                calculateHoverState(e.touches[0].clientX, e.touches[0].clientY);
            }
        };

        // --- UNIFIED RELEASE HANDLER ---
        // This runs on Mouse Up OR Touch End (Taps)
        const handleRelease = (e) => {
            // Stop ghost clicks on mobile after touch events
            if (e.cancelable && e.type !== 'mouseup') e.preventDefault(); 
            e.stopPropagation();

            // Extract exact lift-off coordinates
            let clientX, clientY;
            if (e.changedTouches && e.changedTouches.length > 0) {
                clientX = e.changedTouches[0].clientX;
                clientY = e.changedTouches[0].clientY;
            } else {
                clientX = e.clientX;
                clientY = e.clientY;
            }

            // Calculate EXACTLY where the finger lifted to determine selection on tap
            const state = calculateHoverState(clientX, clientY);
            const timeSinceMount = Date.now() - mountTimeRef.current;

            if (state.isOutside) {
                onClose();
            } else if (state.isCenter) {
                // If tapped briefly on center (or long pressed and released instantly), make it sticky
                if (timeSinceMount < 300 && !isSticky) {
                    setIsSticky(true); 
                    return; 
                }
                onClose(); 
            } else if (state.index !== null) {
                onSelect(menuItems[state.index].label); 
            } else {
                onClose();
            }
        };

        window.addEventListener('mousemove', onMouseMove);
        window.addEventListener('touchmove', onTouchMove, { passive: false });
        window.addEventListener('mouseup', handleRelease);
        window.addEventListener('touchend', handleRelease);
        
        return () => {
            window.removeEventListener('mousemove', onMouseMove);
            window.removeEventListener('touchmove', onTouchMove);
            window.removeEventListener('mouseup', handleRelease);
            window.removeEventListener('touchend', handleRelease);
        };
    }, [menuStyle, onClose, onSelect, menuItems, SECTOR_ANGLE, isSticky]);

    return (
        <>
            <style>{radialStyles}</style>
            <div className="radial-overlay">
                <div className="radial-menu-wrapper" style={{ left: menuStyle.left, top: menuStyle.top }}>
                    <svg width={MENU_SIZE} height={MENU_SIZE} viewBox={`0 0 ${MENU_SIZE} ${MENU_SIZE}`}>
                        {menuItems.map((item, index) => {
                            const startAngle = (index * SECTOR_ANGLE) + (GAP / 2);
                            const endAngle = ((index + 1) * SECTOR_ANGLE) - (GAP / 2);
                            const pathData = createDonutSlice(CENTER, CENTER, startAngle, endAngle, INNER_RADIUS + 2, OUTER_RADIUS);
                            const midAngle = (startAngle + endAngle) / 2;
                            const pos = polarToCartesian(CENTER, CENTER, ICON_RADIUS, midAngle);
                            
                            const statusClass = item.label.replace(/\s+/g, '-');
                            const isActive = activeIndex === index;

                            return (
                                <g 
                                    key={item.label} 
                                    className={`sector-group ${statusClass} ${isActive ? 'active' : ''}`}
                                >
                                    <path d={pathData} className="sector-path" />
                                    <g>
                                        <foreignObject x={pos.x - 15} y={pos.y - 24} width="30" height="30" className="sector-icon-container">
                                            <item.icon size={22} />
                                        </foreignObject>
                                        <text x={pos.x} y={pos.y + 14} className="sector-text">
                                            {item.label.replace(/Waiting On/g, 'Wait')}
                                        </text>
                                    </g>
                                </g>
                            );
                        })}
                        <g className={`center-group ${isCenterActive ? 'active' : ''}`}>
                            <circle cx={CENTER} cy={CENTER} r={INNER_RADIUS - 5} className="center-circle" />
                            <foreignObject x={CENTER - 12} y={CENTER - 12} width="24" height="24" className="center-icon">
                                <FiX size={24} />
                            </foreignObject>
                        </g>
                    </svg>
                </div>
            </div>
        </>
    );
};

export default RadialStatusMenu;